/* eslint-disable  react/no-multi-comp */
/*  eslint-disable react/display-name */
/*  eslint-disable react/prop-types */

import React from 'react';
import { Tag, Tooltip } from '@app/ui';
import getPlatformClass from '@app/utils/getPlatformClass';
import * as format from '@app/utils/formatters';
import { platformOptions } from './socialbenchmarking-filters';

const allowedPlatforms = [1, 2, 3, 23];

const getValueSymbol = (value = null) => {
    if (!value || value < 0) return '';
    return '+';
};

const getPlatformNames = (platformIds: number[]) => {
    if (!platformIds || !platformIds.length) return 'N/A';

    const platformNamesArray = platformIds
        .filter(id => allowedPlatforms.includes(id))
        .map(id => {
            const platform = platformOptions.find(
                option => option.value === id,
            );
            return platform ? platform.label : id.toString();
        })
        .sort(); // Sort alphabetically to ensure consistent ordering

    return platformNamesArray.join('-');
};

const buildHeaders = ({
    socialMetric,
    isFollowerMetricCurrent = false,
    isPostMetricCurrent = false,
    countPerDayAccessor,
    countPerDayHeader,
    countPerDayUpdatedAccessor,
    countPerDayUpdatedHeader,
    t,
}) => {
    return [
        {
            title: t('socialBenchmarking.table.columns.rank'),
            dataIndex: 'rank',
            key: 'rank',
            width: 40,
            render: (value: number | 'N/A') => {
                return value;
            },
        },
        {
            title: t('socialBenchmarking.table.columns.accountGroup'),
            dataIndex: 'accountGroupName',
            key: 'accountGroupName',
            width: 150,
            render: (value: string) => value || '-',
        },
        {
            title: t('socialBenchmarking.table.columns.platform'),
            dataIndex: 'platformIds',
            key: 'platformIds',
            width: 40,
            render: (platformIds: number[], record: any) => {
                if (!platformIds || !platformIds.length) return '-';

                const filteredPlatformIds = platformIds
                    .filter(id => allowedPlatforms.includes(id))
                    .sort((a, b) => a - b);

                // Get accounts for this account group
                const accounts = record.accounts || [];

                return (
                    <div style={{ display: 'flex', gap: '4px' }}>
                        {filteredPlatformIds.map(platformId => {
                            const platformClass = getPlatformClass(platformId);

                            // Find the account for this platform
                            const accountForPlatform = accounts.find(
                                (account: any) =>
                                    account.platformId === platformId,
                            );

                            const iconElement = (
                                <i
                                    key={platformId}
                                    className={platformClass}
                                    style={{
                                        fontSize: '16px',
                                        color: '#009FDF',
                                    }}
                                />
                            );

                            // If we have an account for this platform, wrap with tooltip
                            if (
                                accountForPlatform &&
                                accountForPlatform.username
                            ) {
                                return (
                                    <Tooltip
                                        key={platformId}
                                        title={`@${accountForPlatform.username}`}
                                        placement="top"
                                    >
                                        {iconElement}
                                    </Tooltip>
                                );
                            }

                            return iconElement;
                        })}
                    </div>
                );
            },
            // Custom render function for CSV export that returns platform names
            renderForExport: (platformIds: number[]) => {
                return getPlatformNames(platformIds);
            },
        },
        {
            title:
                socialMetric ||
                t('socialBenchmarking.table.columns.metric', {
                    metric: 'Post Volume',
                }),
            dataIndex: 'itemCount',
            key: 'itemCount',
            width: 120,
            render: (value: number) => {
                if (value == null) {
                    return (
                        <Tag color="#f50">
                            {t('socialBenchmarking.table.dataNotAvailable')}
                        </Tag>
                    );
                }
                return format.decimal(value);
            },
        },
        countPerDayAccessor &&
            countPerDayHeader && {
                title: countPerDayHeader,
                dataIndex: countPerDayAccessor,
                key: countPerDayAccessor,
                width: 150,
                render: (value: number) => {
                    if (value == null) {
                        return (
                            <Tag color="#f50">
                                {t('socialBenchmarking.table.dataNotAvailable')}
                            </Tag>
                        );
                    }

                    // For follower metric, show as green/red badge like week over week change
                    if (
                        isFollowerMetricCurrent &&
                        countPerDayAccessor === 'newFollowers'
                    ) {
                        const isPositive = value >= 0;
                        const displayValue =
                            getValueSymbol(value) + format.decimal(value, true);

                        return (
                            <Tag
                                color={isPositive ? '#52c41a' : '#ff4d4f'}
                                style={{
                                    borderRadius: '6px',
                                    border: `1px solid ${isPositive ? '#52c41a' : '#ff4d4f'}`,
                                    backgroundColor: isPositive
                                        ? '#f6ffed'
                                        : '#fff2f0',
                                    color: isPositive ? '#52c41a' : '#ff4d4f',
                                    padding: '2px 8px',
                                }}
                            >
                                {displayValue}
                            </Tag>
                        );
                    }

                    // For other metrics, show as regular value
                    return (
                        <div>
                            {isFollowerMetricCurrent && getValueSymbol(value)}
                            {format.decimal(value, true)}
                        </div>
                    );
                },
            },
        !isFollowerMetricCurrent &&
            countPerDayUpdatedAccessor &&
            countPerDayUpdatedHeader && {
                title: countPerDayUpdatedHeader,
                dataIndex: countPerDayUpdatedAccessor,
                key: countPerDayUpdatedAccessor,
                width: 150,
                render: (value: number) => {
                    if (value == null) {
                        return (
                            <Tag color="#f50">
                                {t('socialBenchmarking.table.dataNotAvailable')}
                            </Tag>
                        );
                    }

                    const isPositive = value >= 0;
                    const displayValue = isPostMetricCurrent
                        ? getValueSymbol(value) + format.decimal(value, true)
                        : format.decimal(value, true);

                    // Only display as Tag if isFollowerMetricCurrent or isPostMetricCurrent
                    if (isFollowerMetricCurrent || isPostMetricCurrent) {
                        return (
                            <Tag
                                color={isPositive ? '#52c41a' : '#ff4d4f'}
                                style={{
                                    borderRadius: '6px',
                                    border: `1px solid ${isPositive ? '#52c41a' : '#ff4d4f'}`,
                                    backgroundColor: isPositive
                                        ? '#f6ffed'
                                        : '#fff2f0',
                                    color: isPositive ? '#52c41a' : '#ff4d4f',
                                    padding: '2px 8px',
                                }}
                            >
                                {displayValue}
                            </Tag>
                        );
                    }

                    // Return plain text if not follower or post metric
                    return displayValue;
                },
            },
        {
            title: t('socialBenchmarking.table.columns.engagementRate'),
            dataIndex: 'engagementRate',
            key: 'engagementRate',
            width: 180,
            render: (value: number) => {
                if (value == null) {
                    return (
                        <Tag color="#f50">
                            {t('socialBenchmarking.table.dataNotAvailable')}
                        </Tag>
                    );
                }
                return format.decimal(value);
            },
        },
    ].filter(Boolean);
};

export default buildHeaders;
